import { chain, pick, isEmpty, omit, contains } from 'underscore';
import { include } from 'underscore.string';
import { convertToCampaign, convertToAdGroup, CampaignTypeByODataValue } from '@bingads-webui-campaign-react/shell-ui-common';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { isXandrCampaign } from '@bingads-webui-campaign-react/constants';

const BUDGET_SUGGESTION = 'BudgetSuggestions';
const devicesMap = i18n => ({
  Smartphones: i18n.getString(_TL_('Smartphone')),
  Computers: i18n.getString(_TL_('Computer')),
  Tablets: i18n.getString(_TL_('Tablet')),
  ConnectedTVs: i18n.getString(_TL_('CTV')),
});

const statusMap = {
  active: 'Active',
  paused: 'Paused',
};

export const convertToSummaryBarCampaign = (campaignFromOData) => {
  const campaign = omit(convertToCampaign(campaignFromOData), 'rawStatus');
  campaign.status = campaign.status.toLowerCase();
  const {
    Budget, BiddingScheme, ExperimentId, CampaignSubType, DeliveryStatus, DeliveryStatusDetails,
  } = campaignFromOData;

  return {
    ...campaign,
    currentCampaignType: campaign.campaignType,
    budget: Budget,
    hasBudgetLandscape: contains(DeliveryStatusDetails, BUDGET_SUGGESTION) ||
      DeliveryStatus === BUDGET_SUGGESTION,
    bidStrategy: BiddingScheme.Type,
    isExperiment: ExperimentId !== null,
    campaignSubType: CampaignSubType,
  };
};

export const getAdGroupDisplayBiddingStrategyType = (adGroupFromOData) => {
  if (adGroupFromOData.DisplayBiddingStrategyType === 'InheritFromParent') {
    return adGroupFromOData.Campaign.BiddingScheme && adGroupFromOData.Campaign.BiddingScheme.Type;
  }
  return adGroupFromOData.DisplayBiddingStrategyType;
};

export const convertToSummaryBarAdGroup = (adGroupFromOData) => {
  const adGroup = convertToAdGroup(adGroupFromOData);
  adGroup.status = adGroup.status.toLowerCase();
  const bidMapping = {
    ManualCpc: 'SearchBid',
    ManualCpv: 'CpvBid',
    ManualCpm: 'CpmBid',
  };
  const bidKey = (adGroupFromOData.Campaign.BiddingScheme.Type && bidMapping[adGroupFromOData.Campaign.BiddingScheme.Type]) || 'SearchBid';

  return {
    ...adGroup,
    displayBiddingStrategyType: getAdGroupDisplayBiddingStrategyType(adGroupFromOData),
    currentCampaignType: CampaignTypeByODataValue[adGroupFromOData.Campaign.CampaignType],
    campaignId: adGroupFromOData.Campaign.Id,
    campaignName: adGroupFromOData.Campaign.Name,
    bid: adGroupFromOData[bidKey],
    bidStrategy: adGroupFromOData.Campaign.BiddingScheme.Type,
    isExperiment: adGroupFromOData.Campaign.ExperimentId !== null,
    campaignSubType: adGroupFromOData.Campaign.CampaignSubType,
  };
};

export const convertToSummaryBarExperimentCampaign = (experimentFromOData, campaign) => ({
  ...campaign,
  startDate: experimentFromOData.StartDate,
  endDate: experimentFromOData.EndDate,
  originalCampaignName: experimentFromOData.BaseCampaignName,
  originalCampaignId: experimentFromOData.BaseCampaignId,
  status: experimentFromOData.Status,
  experimentType: experimentFromOData.ExperimentType,
});

const getNameForRadiusLocation = (i18n, lat, long) => {
  const latComputed = Math.round(lat * 100) / 100;
  const longComputed = Math.round(long * 100) / 100;

  return curlyTemplate(i18n.getString(_TL_('Lat: {{Latitude}}  Long: {{Longitude}}')))({
    Latitude: latComputed,
    Longitude: longComputed,
  });
};

const getCriterion = (criterionType, criterionFromOData) => chain(criterionFromOData)
  .filter(item => item && item['@odata.type'] && !include(item['@odata.type'], 'Negative'))
  .map(item => pick(item, 'Criterion'))
  .filter(item => item && item.Criterion && item.Criterion['@odata.type'] === `#Model.${criterionType}Criterion`);

export const convertToSummaryBarLocationsAndDevices = (criterionFromOData, i18n, parentResults = null, campaignSubType = null) => {
  let results = {
    locations: [],
    devices: i18n.getString(_TL_('None')),
  };

  if (!criterionFromOData && !parentResults) {
    return results;
  }

  if (criterionFromOData && criterionFromOData.dataNotReady) {
    return {
      locations: ['-'],
      devices: ['-'],
    };
  }

  if (criterionFromOData) {
    const locationCriterion = getCriterion('Location', criterionFromOData.value)
      .map(item => pick(item.Criterion, 'Name', 'LocationType'))
      .sortBy('Name')
      .sortBy('LocationType')
      .map(item => item.Name)
      .value();

    const radiusCriterion = getCriterion('Radius', criterionFromOData.value)
      .map(item => pick(item.Criterion, 'RadiusUnit', 'Name', 'Latitude', 'Longitude', 'Radius'))
      .map((item) => {
        const unit = item.RadiusUnit.toLowerCase() === 'miles' ? i18n.getString(_TL_('miles'))
          : i18n.getString(_TL_('kilometers'));
        const location = isEmpty(item.Name) ? getNameForRadiusLocation(i18n, item.Latitude, item.Longitude) : item.Name;

        return curlyTemplate(i18n.getString(_TL_('{{radius}} {{unit}} around {{location}}')))({
          radius: item.Radius,
          unit,
          location,
        });
      })
      .value();

    const devicesI18nMap = devicesMap(i18n);
    const deviceCriterion = chain(criterionFromOData.value)
      .map(item => pick(item, 'Criterion', 'CriterionBid'))
      .filter(item => item && item.Criterion &&
        item.Criterion['@odata.type'] === '#Model.DeviceCriterion' &&
        item.CriterionBid &&
        ((isXandrCampaign(campaignSubType) && item.CriterionBid.Multiplier !== -100) || (!isXandrCampaign(campaignSubType) && item.CriterionBid.Multiplier !== 0)))
      .map(item => devicesI18nMap[item.Criterion.DeviceName])
      .value();

    results = {
      locations: locationCriterion.concat(radiusCriterion),
      devices: deviceCriterion.length ? [deviceCriterion.join(',')] : [],
    };
  }

  if (parentResults) {
    results = {
      locations: results.locations.length ? results.locations : parentResults.locations,
      devices: results.devices.length ? results.devices : parentResults.devices,
    };
  }

  return results;
};

export const convertToSummaryBarAudiences = (audienceFromOData) => {
  let result = [];

  if (audienceFromOData && audienceFromOData.value) {
    result = chain(audienceFromOData.value).map(item => item && item.AudienceCriterion)
      .map(item => item && item.AudienceName)
      .sortBy('AudienceName')
      .value();
    result.totalCount = audienceFromOData['@odata.count'];
  } else if (audienceFromOData && audienceFromOData.dataNotReady) {
    result.totalCount = 1;
    result = ['-'];
  }
  return result;
};

export const convertToSummaryBarStatusPatch = patch => ({
  Status: statusMap[patch.status],
});
